/**
 * Stato del Live Timer lato client.
 *
 * Tiene insieme quattro cose che nella panchina devono funzionare come una
 * sola: il cronometro, la coda di invio, la sessione e lo stato della partita.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  calcolaCronometro,
  calcolaStatoCampo,
  calcolaTabellino,
  secondoGiallo,
  verificaIngresso,
  verificaUscita,
} from '@calcio/shared';
import type { Evento, EventoIn, Formato, Sospensione, TipoEvento } from '@calcio/shared';
import { ErroreApi, chiama } from '../api/client.js';

const INTERVALLO_BATTITO_MS = 15_000;
const RITENTATIVI_MS = [1_000, 2_000, 5_000, 10_000, 20_000];
/** Oltre questa attesa il messaggio cambia tono e suggerisce carta e penna. */
const SOGLIA_ALLARME_MS = 120_000;

export interface RispostaStato {
  partita: {
    id: string;
    stato: 'programmata' | 'formazione' | 'in_corso' | 'conclusa' | 'annullata';
    avversario: string;
    inCasa: boolean;
    squadraStagioneId: string;
  };
  formato: Formato;
  eventi: Evento[];
  sospensioni: Sospensione[];
  sessione: { id: string; accountId: string } | null;
}

export type ModoTimer = 'comando' | 'lettura' | 'perso';

/**
 * Scarto tra l'orologio del dispositivo e quello del server.
 *
 * Il minuto si calcola da `adesso - inizio_periodo`, ma l'ora di un telefono
 * puo essere sbagliata di minuti. Si misura lo scarto a ogni battito e lo si
 * applica: senza, due dispositivi mostrano due minuti diversi e nessuno
 * capisce perche.
 */
function useOrologioServer() {
  const scarto = useRef(0);

  const allinea = useCallback((orarioServer: string, partenza: number) => {
    const andataRitorno = Date.now() - partenza;
    const stimaServer = new Date(orarioServer).getTime() + andataRitorno / 2;
    scarto.current = stimaServer - Date.now();
  }, []);

  const adesso = useCallback(() => new Date(Date.now() + scarto.current), []);
  return { allinea, adesso, scarto };
}

/** Istante di gioco di un evento: periodo, minuto e secondo, e l'ora vera. */
export type Momento = Pick<EventoIn, 'periodo' | 'minuto' | 'secondo' | 'orarioReale'>;

export function useTimerPartita(partitaId: string) {
  const [stato, setStato] = useState<RispostaStato | null>(null);
  const [sessioneId, setSessioneId] = useState<string | null>(null);
  const [modo, setModo] = useState<ModoTimer>('lettura');
  const [errore, setErrore] = useState<string | null>(null);
  const [inCoda, setInCoda] = useState(0);
  const [attesaDaMs, setAttesaDaMs] = useState(0);
  const [tick, setTick] = useState(0);

  const { allinea, adesso } = useOrologioServer();

  /** Eventi toccati ma non ancora confermati dal server. */
  const coda = useRef<EventoIn[]>([]);
  /**
   * Eventi annullati dal toast. Un evento puo essere gia in viaggio col
   * battito quando si tocca «Annulla»: finche il server non lo restituisce
   * cancellato resta qui, nascosto in locale, e ogni risposta che lo riporta
   * vivo fa ripartire la cancellazione.
   */
  const annullati = useRef<Set<string>>(new Set());
  const primoFallimento = useRef<number | null>(null);
  const tentativo = useRef(0);

  // --- Caricamento iniziale --------------------------------------------------

  useEffect(() => {
    let vivo = true;
    (async () => {
      const s = await chiama<RispostaStato>(`/partite/${partitaId}/stato`);
      if (!vivo) return;
      setStato(s);
      if (!s.sessione) return;

      setSessioneId(s.sessione.id);

      // Una pagina ricaricata non deve trovarsi in sola lettura se la
      // sessione attiva e gia la propria: e il caso normale di chi ricarica
      // durante la partita, non un takeover da confermare.
      const io = await chiama<{ accountId: string }>('/io').catch(() => null);
      if (vivo && io && io.accountId === s.sessione.accountId) setModo('comando');
    })().catch((e: ErroreApi) => vivo && setErrore(e.message));
    return () => {
      vivo = false;
    };
  }, [partitaId]);

  // --- Cronometro ------------------------------------------------------------

  // Un tick al secondo per ridisegnare. Il valore non viene incrementato:
  // e sempre ricalcolato dagli orari, cosi una scheda sospesa o una pagina
  // ricaricata riprendono al minuto giusto.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const eventiVisibili = useMemo(() => {
    if (!stato) return [];
    const daServer = stato.eventi;
    const noti = new Set(daServer.map((e) => e.id));
    const locali: Evento[] = coda.current
      .filter((e) => !noti.has(e.id))
      .map((e) => ({ ...e, cancellatoIl: null }));
    const annullatoIl = new Date().toISOString();
    return [...daServer, ...locali].map((e) =>
      annullati.current.has(e.id) && !e.cancellatoIl ? { ...e, cancellatoIl: annullatoIl } : e,
    );
    // La coda e un ref che React non osserva: inCoda e il segnale che e cambiata.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stato, inCoda]);

  const attivi = useMemo(() => eventiVisibili.filter((e) => !e.cancellatoIl), [eventiVisibili]);

  const cronometro = useMemo(
    () => (stato ? calcolaCronometro(stato.formato, attivi, stato.sospensioni, adesso()) : null),
    // adesso() e stabile: tick e il segnale che ogni secondo fa ricalcolare il minuto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stato, attivi, adesso, tick],
  );

  const campo = useMemo(
    () => (stato ? calcolaStatoCampo(stato.formato, attivi) : null),
    [stato, attivi],
  );

  const tabellino = useMemo(
    // I minuti di chi e in campo si contano al minuto effettivo, recuperi compresi.
    () =>
      stato && cronometro
        ? calcolaTabellino(stato.formato, attivi, cronometro.minutoEffettivo)
        : null,
    [stato, attivi, cronometro],
  );

  // --- Coda di invio ---------------------------------------------------------

  const applicaRisposta = useCallback(
    (r: RispostaStato & { orarioServer?: string }, partenza: number) => {
      if (r.orarioServer) allinea(r.orarioServer, partenza);
      for (const e of r.eventi) {
        if (!annullati.current.has(e.id)) continue;
        if (e.cancellatoIl) annullati.current.delete(e.id);
        else void cancellaAnnullato.current(e.id);
      }
      setStato((precedente) => ({ ...(precedente ?? r), ...r }));
      const confermati = new Set(r.eventi.map((e) => e.id));
      coda.current = coda.current.filter((e) => !confermati.has(e.id));
      setInCoda(coda.current.length);
      primoFallimento.current = null;
      tentativo.current = 0;
      setAttesaDaMs(0);
      setErrore(null);
    },
    [allinea],
  );

  /**
   * Cancella sul server un evento annullato. Un rifiuto non si mostra: resta
   * tra gli annullati, e la prossima risposta che lo riporta vivo ritenta.
   */
  const cancellaAnnullato = useRef<(eventoId: string) => Promise<void>>(async () => {});
  cancellaAnnullato.current = async (eventoId: string) => {
    const partenza = Date.now();
    const r = await chiama<RispostaStato>(`/partite/${partitaId}/eventi/${eventoId}`, {
      metodo: 'DELETE',
    }).catch(() => null);
    if (r) applicaRisposta(r, partenza);
  };

  const svuotaCoda = useCallback(async () => {
    if (!sessioneId || modo !== 'comando') return;

    const partenza = Date.now();
    try {
      const r = await chiama<RispostaStato & { orarioServer: string }>(
        `/partite/${partitaId}/battito`,
        { metodo: 'POST', corpo: { sessioneId, eventi: coda.current } },
      );
      applicaRisposta(r, partenza);
    } catch (e) {
      if (e instanceof ErroreApi && e.motivo === 'sessione_persa') {
        // Il controllo e passato altrove mentre eravamo disconnessi. La coda
        // NON va inviata: si otterrebbero eventi doppi registrati da due
        // panchine. Restano in attesa di una decisione esplicita.
        setModo('perso');
        setErrore('Un altro dispositivo ha preso il controllo della partita.');
        return;
      }
      if (e instanceof ErroreApi && e.stato >= 400 && e.stato < 500) {
        // Errore di merito: ritentare non cambierebbe nulla.
        setErrore(e.message);
        coda.current = [];
        setInCoda(0);
        return;
      }
      primoFallimento.current ??= Date.now();
      setAttesaDaMs(Date.now() - primoFallimento.current);
      tentativo.current = Math.min(tentativo.current + 1, RITENTATIVI_MS.length - 1);
    }
  }, [applicaRisposta, modo, partitaId, sessioneId]);

  // Battito regolare. Con la coda piena si ritenta piu spesso, perche
  // l'obiettivo e ridurre la finestra in cui un evento esiste solo sul
  // telefono.
  useEffect(() => {
    if (modo !== 'comando') return;
    const attesa =
      coda.current.length > 0 ? RITENTATIVI_MS[tentativo.current]! : INTERVALLO_BATTITO_MS;
    const id = setTimeout(svuotaCoda, attesa);
    return () => clearTimeout(id);
  }, [modo, svuotaCoda, inCoda, tick]);

  // --- Registrazione ---------------------------------------------------------

  const nuovoId = () =>
    typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  /**
   * Registra un evento. L'identificativo nasce qui, sul dispositivo: se la
   * risposta si perde e la coda ritenta, il server riconosce l'evento gia
   * salvato e non lo duplica.
   *
   * L'inserimento e ottimistico: l'evento compare subito nell'elenco e nel
   * punteggio. Un pulsante che aspetta la rete prima di reagire viene premuto
   * due volte.
   *
   * Restituisce gli eventi messi in coda (il giallo porta con se il rosso),
   * o nessuno se da questo dispositivo non si comanda.
   *
   * Con `quando` l'evento prende un istante gia fissato invece di adesso:
   * serve a un gol che aspetta la scelta dell'assist, e resta al minuto in
   * cui e stato segnato.
   */
  const registra = useCallback(
    (tipo: TipoEvento, giocatoreId: string | null, quando?: Momento): EventoIn[] => {
      if (!stato || !cronometro || modo !== 'comando') return [];

      const evento: EventoIn = {
        id: nuovoId(),
        tipo: tipo as EventoIn['tipo'],
        giocatoreId,
        ...(quando ?? {
          periodo: cronometro.periodo,
          minuto: cronometro.minuto,
          secondo: cronometro.secondo,
          orarioReale: adesso().toISOString(),
        }),
      };

      // Il secondo giallo porta con se il rosso, nello stesso invio: il
      // giocatore esce subito dal campo anche senza rete, e il server vede il
      // rosso nel lotto e non ne aggiunge un altro.
      const giallo =
        tipo === 'cartellino_giallo' && giocatoreId
          ? secondoGiallo([...attivi, evento], giocatoreId)
          : null;
      const rosso: EventoIn[] = giallo
        ? [
            {
              ...evento,
              id: nuovoId(),
              tipo: 'cartellino_rosso',
              periodo: giallo.periodo,
              minuto: giallo.minuto,
              secondo: giallo.secondo,
              orarioReale: giallo.orarioReale,
            },
          ]
        : [];

      coda.current = [...coda.current, evento, ...rosso];
      setInCoda(coda.current.length);
      void svuotaCoda();
      return [evento, ...rosso];
    },
    [adesso, attivi, cronometro, modo, stato, svuotaCoda],
  );

  /** L'istante di gioco di adesso, da passare piu tardi a `registra`. */
  const momento = useCallback(
    (): Momento | null =>
      cronometro
        ? {
            periodo: cronometro.periodo,
            minuto: cronometro.minuto,
            secondo: cronometro.secondo,
            orarioReale: adesso().toISOString(),
          }
        : null,
    [adesso, cronometro],
  );

  /**
   * Un cambio: chi esce, chi entra, o tutti e due. Le stesse guardie girano
   * poi anche sul server: qui servono a dare un messaggio immediato invece di
   * far partire una richiesta destinata a fallire. Chi entra si verifica con
   * chi esce gia fuori, perche col campo pieno l'ingresso da solo non passa.
   *
   * Uscita e ingresso prendono lo stesso minuto, che e cio che li fa leggere
   * come una sostituzione, e l'ingresso un millisecondo dopo, per restare in
   * fila dietro l'uscita.
   */
  const cambia = useCallback(
    (
      esceId: string | null,
      entraId: string | null,
    ): { ok: boolean; messaggio?: string; eventi: EventoIn[] } => {
      if (!stato || !campo || !cronometro) return { ok: false, eventi: [] };
      let dopo = campo;
      if (esceId) {
        const esito = verificaUscita(campo, esceId);
        if (!esito.ok) return { ok: false, messaggio: esito.messaggio, eventi: [] };
        dopo = { ...campo, inCampo: new Set([...campo.inCampo].filter((id) => id !== esceId)) };
      }
      if (entraId) {
        const esito = verificaIngresso(stato.formato, dopo, entraId);
        if (!esito.ok) return { ok: false, messaggio: esito.messaggio, eventi: [] };
      }

      const quando = momento()!;
      const eventi = [
        ...(esceId ? registra('uscita', esceId, quando) : []),
        ...(entraId
          ? registra('ingresso', entraId, {
              ...quando,
              orarioReale: new Date(
                Date.parse(quando.orarioReale) + (esceId ? 1 : 0),
              ).toISOString(),
            })
          : []),
      ];
      return { ok: eventi.length > 0, eventi };
    },
    [campo, cronometro, momento, registra, stato],
  );

  const cancella = useCallback(
    async (eventoId: string) => {
      const partenza = Date.now();
      const r = await chiama<RispostaStato>(`/partite/${partitaId}/eventi/${eventoId}`, {
        metodo: 'DELETE',
      });
      applicaRisposta(r, partenza);
    },
    [applicaRisposta, partitaId],
  );

  /**
   * Annulla eventi appena registrati, dal toast. Quelli ancora in coda
   * escono dalla coda; tutti passano comunque dalla cancellazione sul server,
   * che per un evento mai arrivato non fa nulla: un battito gia partito
   * potrebbe salvarlo un attimo dopo.
   */
  const annulla = useCallback((eventoIds: string[]) => {
    const ids = new Set(eventoIds);
    for (const id of ids) annullati.current.add(id);
    coda.current = coda.current.filter((e) => !ids.has(e.id));
    setInCoda(coda.current.length);
    for (const id of ids) void cancellaAnnullato.current(id);
  }, []);

  // --- Sessione --------------------------------------------------------------

  const avvia = useCallback(async () => {
    const partenza = Date.now();
    const r = await chiama<RispostaStato & { sessione: { id: string } }>(
      `/partite/${partitaId}/avvia`,
      { metodo: 'POST' },
    );
    setSessioneId(r.sessione.id);
    setModo('comando');
    applicaRisposta(r, partenza);
  }, [applicaRisposta, partitaId]);

  /**
   * Prende il controllo. Se la sessione in corso e ancora viva il server
   * risponde 409 con chi la detiene: si chiede conferma e si ripete.
   */
  const prendiControllo = useCallback(
    async (conferma = false) => {
      try {
        const r = await chiama<{ sessione: { id: string } }>(`/partite/${partitaId}/controllo`, {
          metodo: 'POST',
          corpo: { conferma },
        });
        setSessioneId(r.sessione.id);
        setModo('comando');
        setErrore(null);
        return { preso: true as const };
      } catch (e) {
        if (e instanceof ErroreApi && e.stato === 409) {
          const c = e.corpo as { detenutaDa: string; inattivaDaMs: number };
          return { preso: false as const, ...c };
        }
        throw e;
      }
    },
    [partitaId],
  );

  /**
   * I comandi del cronometro partono dopo la coda: il server verifica chi e
   * in campo alla ripartenza, e un "Entra" toccato un attimo prima deve
   * arrivargli prima del comando. Gli errori salgono a chi chiama, che li
   * mostra: un periodo rifiutato in silenzio sembra un tocco perso.
   */
  const comandoPeriodo = useCallback(
    async (azione: 'inizia' | 'termina' | 'annulla', inferiorita = false) => {
      if (!sessioneId) return;
      await svuotaCoda();
      const partenza = Date.now();
      const r = await chiama<RispostaStato>(`/partite/${partitaId}/periodo/${azione}`, {
        metodo: 'POST',
        corpo: { sessioneId, inferiorita },
      });
      applicaRisposta(r, partenza);
    },
    [applicaRisposta, partitaId, sessioneId, svuotaCoda],
  );

  const sospensione = useCallback(
    async (azione: 'apri' | 'chiudi', inferiorita = false) => {
      if (!sessioneId) return;
      await svuotaCoda();
      const partenza = Date.now();
      const r = await chiama<RispostaStato>(`/partite/${partitaId}/sospensione`, {
        metodo: 'POST',
        corpo: { sessioneId, azione, inferiorita },
      });
      applicaRisposta(r, partenza);
    },
    [applicaRisposta, partitaId, sessioneId, svuotaCoda],
  );

  return {
    stato,
    cronometro,
    campo,
    tabellino,
    eventi: eventiVisibili,
    modo,
    errore,
    inCoda,
    allarmeInvio: attesaDaMs > SOGLIA_ALLARME_MS,
    azioni: {
      registra,
      momento,
      annulla,
      cambia,
      cancella,
      avvia,
      prendiControllo,
      comandoPeriodo,
      sospensione,
      /** Recepisce la risposta di una richiesta fatta fuori dall'hook. */
      aggiorna: applicaRisposta,
    },
  };
}

/**
 * Tiene acceso lo schermo mentre il timer e in uso.
 *
 * Senza, il telefono si spegne dopo trenta secondi e va sbloccato a ogni
 * evento: e la differenza tra usabile e inutilizzabile. Il permesso decade
 * quando la scheda perde il fuoco, quindi va riacquisito al ritorno.
 */
export function useSchermoAcceso(attivo: boolean): void {
  useEffect(() => {
    if (!attivo || !('wakeLock' in navigator)) return;

    let blocco: WakeLockSentinel | null = null;
    let annullato = false;

    const acquisisci = async () => {
      try {
        blocco = await navigator.wakeLock.request('screen');
      } catch {
        // Batteria bassa o permesso negato: si continua senza.
      }
    };

    const alRitorno = () => {
      if (!annullato && document.visibilityState === 'visible') void acquisisci();
    };

    void acquisisci();
    document.addEventListener('visibilitychange', alRitorno);

    return () => {
      annullato = true;
      document.removeEventListener('visibilitychange', alRitorno);
      void blocco?.release();
    };
  }, [attivo]);
}
